import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import {
  RequestEmailVerification,
  RequestPhoneVerification,
  RequestPhoneVerificationSmsStatus,
  VerifyEmail,
  VerifyPhone,
} from 'connect/saga/handlers/patient/verify';
import { selectDataPatient } from 'connect/selectors';
import { CheckoutLinkButton, FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';
import { formatPhoneNumber } from 'pages/checkout/utils/phoneUtils';

import { StateIndicator } from './StateIndicator';
import { BodyText, CodeInput, CodeInputStateWrapper, CodeInputWrapper, CodeSection, InputWrapper, InstructionsWrapper, ResendCodeText, ResendCodeWrapper, VerifyStepWrapper } from './Verify-styled-components';
import { ContactText } from './Verify-styled-components';

const analytics = new Analytics();

export enum CodeInputState {
  Phone,
  Email,
}

const ResendCodeContent = {
  [CodeInputState.Phone]: 'Resend Message',
  [CodeInputState.Email]: 'Send a new email',
};

export const Verify = () => {
  const dispatch = useDispatch();
  const patient = useSelector(selectDataPatient);
  const { email, mobilePhone: phoneNumber } = useMemo(() => patient || {}, [patient]);
  const analyticsData = useCheckoutRedesignAnalyticsData();

  const [codeInputState, setCodeInputState] = useState<CodeInputState>(CodeInputState.Phone);
  const [phoneCode, setPhoneCode] = useState<string>('');
  const [emailCode, setEmailCode] = useState<string>('');
  const [smsDeliveryFailed, setSmsDeliveryFailed] = useState(false);

  const [showResendPhoneCode, setShowResendPhoneCode] = useState(false);
  const [showResendEmailCode, setShowResendEmailCode] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const showCodeTimeout = setTimeout(() => {
      if (codeInputState === CodeInputState.Phone) {
        setShowResendPhoneCode(true);
      } else {
        setShowResendEmailCode(true);
      }
    }, 5000);

    return () => clearTimeout(showCodeTimeout);
  }, [codeInputState]);

  const { goToNextStep } = useNextCheckoutStep();

  const phoneVerificationLoading = useSelector(VerifyPhone.loading!.selector);
  const phoneVerificationError = useSelector(VerifyPhone.error!.selector);
  const phoneVerificationSuccess = useSelector(VerifyPhone.success!.selector);

  const emailVerificationLoading = useSelector(VerifyEmail.loading!.selector);
  const emailVerificationError = useSelector(VerifyEmail.error!.selector);
  const emailVerificationSuccess = useSelector(VerifyEmail.success!.selector);

  const requestPhoneSuccess = useSelector(RequestPhoneVerification.success!.selector);
  const requestPhoneError = useSelector(RequestPhoneVerification.error!.selector);
  const requestPhoneLoading = useSelector(RequestPhoneVerification.loading!.selector);

  const requestEmailError = useSelector(RequestEmailVerification.error!.selector);
  const requestEmailLoading = useSelector(RequestEmailVerification.loading!.selector);

  const smsStatusLoading = useSelector(RequestPhoneVerificationSmsStatus.loading!.selector);
  const smsStatusValue = useSelector(RequestPhoneVerificationSmsStatus.value!.selector);

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify',
      ...analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  const handlePhoneVerifySuccess = useCallback(() => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify-phone',
      content: {
        status: 'success',
        ...analyticsData.content,
      },
    });

    setCodeInputState(CodeInputState.Email);
    dispatch(VerifyPhone.success!.update(null));
  }, [dispatch, analyticsData]);

  const handleEmailVerifySuccess = useCallback(() => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify-email',
      content: {
        status: 'success',
        ...analyticsData.content,
      },
    });

    dispatch(VerifyEmail.success!.update(null));
    goToNextStep();
  }, [dispatch, goToNextStep, analyticsData]);

  const resendCooldownInterval = useRef<NodeJS.Timeout | null>(null);

  const handleSendCode = useCallback(() => {
    // Request verification requires patient id
    if (!patient?.id) {
      return;
    }

    setPhoneCode('');
    setEmailCode('');

    if (codeInputState === CodeInputState.Phone) {
      const alreadyVerified = patient?.phoneVerifiedAt;
      if (alreadyVerified) {
        handlePhoneVerifySuccess();
        return;
      }

      dispatch(RequestPhoneVerification.createAction('text'));
    }

    if (codeInputState === CodeInputState.Email) {
      const alreadyVerified = patient?.emailVerifiedAt;
      if (alreadyVerified) {
        handleEmailVerifySuccess();
        return;
      }

      dispatch(RequestEmailVerification.createAction());
    }
  }, [
    codeInputState,
    dispatch,
    patient?.id,
    patient?.emailVerifiedAt,
    patient?.phoneVerifiedAt,
    handlePhoneVerifySuccess,
    handleEmailVerifySuccess,
  ]);

  const handleResendCode = useCallback(() => {
    if (resendCooldown > 0) {
      return;
    }

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: codeInputState === CodeInputState.Phone ? 'verify-phone' : 'verify-email',
      content: {
        action: 'resend-code',
        ...analyticsData.content,
      },
    });

    handleSendCode();

    if (resendCooldownInterval.current) {
      clearInterval(resendCooldownInterval.current);
    }

    setResendCooldown(60);
    resendCooldownInterval.current = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
  }, [handleSendCode, resendCooldown, codeInputState, analyticsData]);

  // Send code on mount
  useEffect(() => {
    // TODO this may or may not fix the issue where code isn't sent on mount
    setTimeout(() => {
      handleSendCode();
    }, 100);
  }, [handleSendCode]);

  // Check SMS delivery status
  useEffect(() => {
    if (codeInputState !== CodeInputState.Phone || smsStatusLoading) {
      return;
    }

    if (requestPhoneSuccess) {
      setSmsDeliveryFailed(false);
      dispatch(RequestPhoneVerification.success!.update(null));
      dispatch(RequestPhoneVerificationSmsStatus.createAction());
    }
  }, [codeInputState, requestPhoneSuccess, smsStatusLoading, dispatch]);

  // Handle SMS status response
  useEffect(() => {
    if (smsStatusValue && smsStatusValue.status === 'failed') {
      setSmsDeliveryFailed(true);
      dispatch(RequestPhoneVerification.createAction('call'));
    }
  }, [smsStatusValue, dispatch]);

  const isSendingCode = useMemo(() => {
    if (codeInputState === CodeInputState.Phone) {
      return requestPhoneLoading;
    }

    if (codeInputState === CodeInputState.Email) {
      return requestEmailLoading;
    }

    return false;
  }, [codeInputState, requestPhoneLoading, requestEmailLoading]);

  const isLoadingVerification = useMemo(() => {
    if (codeInputState === CodeInputState.Phone) {
      return phoneVerificationLoading;
    }

    if (codeInputState === CodeInputState.Email) {
      return emailVerificationLoading;
    }

    return false;
  }, [codeInputState, phoneVerificationLoading, emailVerificationLoading]);

  const hasError = useMemo(() => {
    return (codeInputState === CodeInputState.Phone && phoneVerificationError)
      || (codeInputState === CodeInputState.Email && emailVerificationError);
  }, [codeInputState, phoneVerificationError, emailVerificationError]);

  // Handle success state
  useEffect(() => {
    if (isLoadingVerification) {
      return;
    }

    if (codeInputState === CodeInputState.Phone) {
      if (!phoneVerificationSuccess) {
        return;
      }
      handlePhoneVerifySuccess();
    }

    if (codeInputState === CodeInputState.Email) {
      const alreadyVerified = requestEmailError?.includes('email is already verified') || patient?.emailVerifiedAt;
      if (!emailVerificationSuccess && !alreadyVerified) {
        return;
      }
      handleEmailVerifySuccess();
    }
  }, [
    phoneVerificationSuccess,
    emailVerificationSuccess,
    codeInputState,
    goToNextStep,
    dispatch,
    requestPhoneError,
    requestEmailError,
    patient,
    phoneVerificationLoading,
    emailVerificationLoading,
    handlePhoneVerifySuccess,
    handleEmailVerifySuccess,
    isLoadingVerification,
  ]);

  const handleSubmitCode = (code: string) => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: codeInputState === CodeInputState.Phone ? 'verify-phone' : 'verify-email',
      content: {
        action: 'submit-code',
        ...analyticsData.content,
      },
    });

    if (codeInputState === CodeInputState.Phone) {
      dispatch(VerifyPhone.createAction(code));
    } else if (codeInputState === CodeInputState.Email) {
      dispatch(VerifyEmail.createAction(code));
    }

    setPhoneCode('');
    setEmailCode('');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberValue = Number(value);

    const setCode = codeInputState === CodeInputState.Phone ? setPhoneCode : setEmailCode;

    if (!value || Number.isNaN(numberValue)) {
      return;
    }

    if (value.length <= 4) {
      setCode(value);
    }

    if (value.length === 4) {
      handleSubmitCode(value);
    }
  };

  // The fallbacks should never be needed, but just in case
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber) || 'your phone';
  const formattedEmail = email || 'your email';

  const codeDestination = {
    [CodeInputState.Phone]: formattedPhoneNumber,
    [CodeInputState.Email]: formattedEmail,
  }[codeInputState];

  const showResendCode = useMemo(() => {
    return codeInputState === CodeInputState.Phone
      ? showResendPhoneCode
      : showResendEmailCode;
  }, [codeInputState, showResendPhoneCode, showResendEmailCode]);

  const isLoadingOrSendingCode = useMemo(() => {
    return isLoadingVerification || isSendingCode;
  }, [isLoadingVerification, isSendingCode]);

  return (
    <VerifyStepWrapper>
      <StepHeader>
        <StepHeaderText>Keep your account secure</StepHeaderText>
        <StepHeaderSubtext>
          To ensure you're the only one who can access your account,
          we need to verify your phone and email.
        </StepHeaderSubtext>
      </StepHeader>

      <CodeSection>
        <CodeInputStateWrapper>
          <StateIndicator codeInputState={codeInputState} />

          <InstructionsWrapper>
            <BodyText>Enter the code we sent to:</BodyText>

            <ContactText className="sensitive-data">{codeDestination}</ContactText>
          </InstructionsWrapper>
        </CodeInputStateWrapper>

        <InputWrapper>
          <CodeInputWrapper $loading={isLoadingOrSendingCode}>
            <CodeInput
              value={codeInputState === CodeInputState.Phone ? phoneCode : emailCode}
              onChange={handleCodeChange}
              $loading={isLoadingOrSendingCode}
            />
          </CodeInputWrapper>

          {hasError && <FormError>The code you entered is incorrect.</FormError>}

          {smsDeliveryFailed && codeInputState === CodeInputState.Phone && (
            <BodyText>
              We have a delivery issue.
              Please expect a verification call within the next 5 seconds.
            </BodyText>
          )}
        </InputWrapper>

      </CodeSection>

      {showResendCode && (
        <ResendCodeWrapper>
          <ResendCodeText>
            <span>
              Didn't receive anything?{' '}
            </span>
            <CheckoutLinkButton onClick={handleResendCode} disabled={isLoadingVerification || resendCooldown > 0}>
              {codeInputState === CodeInputState.Phone && smsDeliveryFailed
                ? 'Call me again'
                : ResendCodeContent[codeInputState]}
              {resendCooldown > 0 && ` (${resendCooldown}s)`}
            </CheckoutLinkButton>
          </ResendCodeText>

          {codeInputState === CodeInputState.Email
            && <BodyText>Remember to check your spam folder</BodyText>
          }
        </ResendCodeWrapper>
      )}

    </VerifyStepWrapper>
  );
};
